import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  BarChart3, Eye, Users, Globe, TrendingUp, Monitor, Smartphone,
  Tablet, Loader2, ArrowLeft, ExternalLink, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import tamLogo from '@/assets/tam-logo.png';

interface AnalyticsData {
  site: {
    id: string;
    slug: string;
    site_name: string;
    is_published: boolean;
    views_count: number;
    created_at: string;
  };
  summary: {
    totalViews: number;
    uniqueVisitors: number;
    todayViews: number;
  };
  dailyViews: { date: string; count: number }[];
  devices: { name: string; count: number }[];
  browsers: { name: string; count: number }[];
  referrers: { name: string; count: number }[];
  countries: { name: string; count: number }[];
}

const SiteAnalytics = () => {
  const [searchParams] = useSearchParams();
  const [siteId, setSiteId] = useState(searchParams.get('siteId') || '');
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasParams, setHasParams] = useState(false);

  useEffect(() => {
    const sid = searchParams.get('siteId');
    const tok = searchParams.get('token');
    if (sid && tok) {
      setSiteId(sid);
      setToken(tok);
      setHasParams(true);
      fetchAnalytics(sid, tok);
    }
  }, [searchParams]);

  const fetchAnalytics = async (sid: string, tok: string) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/site-analytics?siteId=${encodeURIComponent(sid)}&token=${encodeURIComponent(tok)}`,
        {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
        }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to fetch analytics');

      setAnalytics(data);
    } catch (e: any) {
      console.error('Analytics error:', e);
      setError(e.message);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleManualFetch = () => {
    if (!siteId.trim() || !token.trim()) {
      toast.error('Please enter both Site ID and Edit Token');
      return;
    }
    fetchAnalytics(siteId.trim(), token.trim());
  };

  const maxDailyView = Math.max(...(analytics?.dailyViews.map(d => d.count) || [1]));

  // Auth form if no params
  if (!hasParams && !analytics) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src={tamLogo} alt="TAM" className="h-8 w-8 rounded-full" />
              <span className="text-lg font-bold">TAM Store Analytics</span>
            </div>
            <CardTitle>View Site Analytics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="siteId">Site ID</Label>
              <Input
                id="siteId"
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                placeholder="Enter your site ID"
                className="font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="token">Edit Token</Label>
              <Input
                id="token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Enter your edit token"
                type="password"
                className="font-mono text-sm"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button className="w-full gap-2" onClick={handleManualFetch} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4" />}
              View Analytics
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground text-sm">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">Failed to load analytics</p>
          <Button variant="outline" onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  const deviceIcons: Record<string, any> = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <div className="border-b border-border bg-card px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={tamLogo} alt="TAM" className="h-7 w-7 rounded-full" />
          <div>
            <h1 className="text-sm font-semibold">{analytics.site.site_name}</h1>
            <p className="text-xs text-muted-foreground font-mono">/s/{analytics.site.slug}</p>
          </div>
          <Badge variant={analytics.site.is_published ? 'default' : 'secondary'} className="text-[10px]">
            {analytics.site.is_published ? 'Live' : 'Draft'}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1 text-xs"
            onClick={() => window.open(`/s/${analytics.site.slug}`, '_blank')}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Visit Site
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Total Views', value: analytics.summary.totalViews, icon: Eye, color: 'text-primary' },
            { label: 'Unique Visitors', value: analytics.summary.uniqueVisitors, icon: Users, color: 'text-green-500' },
            { label: 'Today', value: analytics.summary.todayViews, icon: TrendingUp, color: 'text-amber-500' },
          ].map(stat => (
            <Card key={stat.label} className="hover:border-primary/30 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={cn("p-2.5 rounded-xl bg-muted/50")}>
                    <stat.icon className={cn("h-5 w-5", stat.color)} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Views Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Daily Views (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics.dailyViews.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">No views data yet</p>
            ) : (
              <div className="flex items-end gap-1 h-40">
                {analytics.dailyViews.map((day) => (
                  <div
                    key={day.date}
                    className="flex-1 group relative"
                    title={`${day.date}: ${day.count} views`}
                  >
                    <div
                      className="bg-primary/80 hover:bg-primary rounded-t transition-all duration-200 min-h-[2px]"
                      style={{ height: `${Math.max((day.count / maxDailyView) * 100, 2)}%` }}
                    />
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-popover border border-border rounded px-2 py-1 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 shadow-lg">
                      {day.date}: {day.count}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Breakdown Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Devices */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Devices</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics.devices.length === 0 ? (
                <p className="text-xs text-muted-foreground">No data</p>
              ) : (
                <div className="space-y-2">
                  {analytics.devices.map(d => {
                    const Icon = deviceIcons[d.name] || Globe;
                    return (
                      <div key={d.name} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 capitalize">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                          {d.name}
                        </span>
                        <span className="text-muted-foreground font-mono text-xs">{d.count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Browsers */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Browsers</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics.browsers.length === 0 ? (
                <p className="text-xs text-muted-foreground">No data</p>
              ) : (
                <div className="space-y-2">
                  {analytics.browsers.map(b => (
                    <div key={b.name} className="flex items-center justify-between text-sm">
                      <span>{b.name}</span>
                      <span className="text-muted-foreground font-mono text-xs">{b.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Countries */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Countries</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics.countries.length === 0 ? (
                <p className="text-xs text-muted-foreground">No data</p>
              ) : (
                <div className="space-y-2">
                  {analytics.countries.slice(0, 5).map(c => (
                    <div key={c.name} className="flex items-center justify-between text-sm">
                      <span>{c.name}</span>
                      <span className="text-muted-foreground font-mono text-xs">{c.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Referrers */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Referrers</CardTitle>
            </CardHeader>
            <CardContent>
              {analytics.referrers.length === 0 ? (
                <p className="text-xs text-muted-foreground">Direct visits only</p>
              ) : (
                <div className="space-y-2">
                  {analytics.referrers.slice(0, 5).map(r => (
                    <div key={r.name} className="flex items-center justify-between text-sm">
                      <span className="truncate max-w-[120px] text-xs">{r.name}</span>
                      <span className="text-muted-foreground font-mono text-xs">{r.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SiteAnalytics;
